import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, PanelLeftClose } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { MODULES, ROUTE_MODULE_MAP, type AppModule } from './modules.config';
import api from '@/lib/axios';
import logoHorizontal from '@/assets/logo-horizontal-bk.svg';
import logoMark from '@/assets/logo.svg';

const ROL_LABEL: Record<string, string> = {
  [ROL.SUPER_ADMIN]:   'Administrador',
  [ROL.ADMIN_CLINICA]: 'Admin Clínica',
  [ROL.MEDICO]:        'Médico',
  [ROL.MEDICO_ADMIN]:  'Médico Admin',
  [ROL.RECEPCIONISTA]: 'Recepcionista',
  [ROL.ASISTENTE]:     'Asistente',
};

const ALWAYS_ON_MODULES = new Set(['dashboard', 'configuracion', 'vademecum', 'tareas']);

interface FloatingPanel {
  moduleId: string;
  top: number;
}

function hexAlpha(hex: string, alpha: number) {
  const n = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return `${hex}${n}`;
}

function getVisibleSections(module: AppModule, rolId?: string) {
  return module.sections.filter(s => !rolId || s.roles.includes(rolId));
}

const SIDEBAR_BG = '#0f172a';

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario, logout } = useAuthStore();
  const qc = useQueryClient();

  async function handleLogout() {
    try { await api.post('/api/auth/logout'); } catch { /* ignore */ }
    qc.clear();
    logout();
    navigate('/login');
  }

  const [isExpanded, setIsExpanded] = useState(
    () => localStorage.getItem('sidebar-expanded') !== 'false'
  );
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());
  const [floating, setFloating] = useState<FloatingPanel | null>(null);

  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const basePath = '/' + location.pathname.split('/')[1];
  const activeModuleId = ROUTE_MODULE_MAP[basePath] || '';

  useEffect(() => {
    if (activeModuleId) {
      setOpenModules(prev => prev.has(activeModuleId) ? prev : new Set([...prev, activeModuleId]));
    }
  }, [activeModuleId]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', String(isExpanded));
    if (isExpanded) {
      clearTimeout(closeTimer.current);
      setFloating(null);
    }
  }, [isExpanded]);

  function navigateToModule(module: AppModule) {
    const first = getVisibleSections(module, usuario?.rolId)[0];
    if (first) navigate(first.to);
  }

  function toggleModule(id: string) {
    setOpenModules(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleModuleEnter(e: React.MouseEvent, moduleId: string) {
    clearTimeout(closeTimer.current);
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setFloating({ moduleId, top: rect.top });
  }

  function scheduleClose() {
    closeTimer.current = setTimeout(() => setFloating(null), 120);
  }

  const initial = usuario?.nombre?.charAt(0)?.toUpperCase() || 'U';

  const { data: profile } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/api/auth/me').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: configData } = useQuery({
    queryKey: ['configuracion'],
    queryFn: () => api.get('/api/configuracion').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
  const modulosHabilitados: Record<string, boolean> = configData?.modulosHabilitadosJson ?? {};

  // Ahora sí podemos referenciar configData y modulosHabilitados sin TDZ
  const visibleModules = MODULES.filter(m => {
    if (!m.sections.some(s => !usuario?.rolId || s.roles.includes(usuario.rolId))) return false;
    if (ALWAYS_ON_MODULES.has(m.id)) return true;
    if (!configData) return true;
    const hasSomeEnabled = Object.keys(modulosHabilitados).length > 0;
    return hasSomeEnabled ? modulosHabilitados[m.id] !== false : true;
  });

  const floatingModule = floating
    ? visibleModules.find(m => m.id === floating.moduleId)
    : null;

  const fotoUrl = profile?.fotoUrl || usuario?.fotoUrl || null;
  const fotoSrc = fotoUrl
    ? `${fotoUrl}?v=${profile?.updatedAt ? new Date(profile.updatedAt).getTime() : 0}`
    : null;

  return (
    <>
      <aside
        className={cn(
          'relative flex flex-col h-full flex-shrink-0 rounded-[2rem] overflow-hidden shadow-xl',
          'transition-[width] duration-300 ease-in-out',
          isExpanded ? 'w-64' : 'w-16'
        )}
        style={{ backgroundColor: SIDEBAR_BG }}
      >
        {/* ── 1. Logo ── */}
        <div
          className="h-[60px] flex items-center flex-shrink-0 overflow-hidden px-3"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
        >
          {isExpanded ? (
            <>
              <img src={logoHorizontal} alt="Anik" className="h-8 flex-1 object-left object-contain brightness-150" />
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1.5 rounded-lg transition-colors flex-shrink-0"
                style={{ color: 'rgba(255,255,255,0.4)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                title="Contraer menú"
              >
                <PanelLeftClose size={16} />
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsExpanded(true)}
              className="w-full flex items-center justify-center p-1.5 rounded-lg transition-colors"
              style={{ color: 'rgba(255,255,255,0.5)' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              title="Expandir menú"
            >
              <img src={logoMark} alt="Anik" className="h-7 w-7 object-contain brightness-200" />
            </button>
          )}
        </div>

        {/* ── 2. Módulos del sistema médico ── */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-0.5">
          {visibleModules.map(module => {
            const isActive = activeModuleId === module.id;
            const isOpen = openModules.has(module.id);
            const sections = getVisibleSections(module, usuario?.rolId);
            const Icon = module.icon;
            const hasSubMenu = sections.length > 1;

            if (!isExpanded) {
              return (
                <div key={module.id}>
                  {module.dividerBefore && (
                    <div className="my-1.5 mx-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />
                  )}
                  <button
                    onClick={() => navigateToModule(module)}
                    onMouseEnter={e => {
                      handleModuleEnter(e, module.id);
                      if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)';
                    }}
                    onMouseLeave={e => {
                      scheduleClose();
                      if (!isActive) e.currentTarget.style.backgroundColor = isActive ? hexAlpha(module.color, 0.18) : 'transparent';
                    }}
                    className="w-full flex items-center justify-center h-10 rounded-lg transition-colors brightness-200"
                    style={{
                      borderLeft: `3px solid ${isActive ? module.color : 'transparent'}`,
                      backgroundColor: isActive ? hexAlpha(module.color, 0.18) : 'transparent',
                      color: isActive ? module.color : 'rgba(255,255,255,0.55)',
                    }}
                  >
                    <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
                  </button>
                </div>
              );
            }

            return (
              <div key={module.id}>
                {module.dividerBefore && (
                  <div className="my-1.5 mx-1 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />
                )}
                <button
                  onClick={() => hasSubMenu ? toggleModule(module.id) : navigateToModule(module)}
                  className="w-full flex items-center gap-2.5 px-2 py-2.5 rounded-lg transition-colors text-sm text-left brightness-200"
                  style={{
                    borderLeft: `3px solid ${isActive ? module.color : 'transparent'}`,
                    backgroundColor: isActive ? hexAlpha(module.color, 0.18) : 'transparent',
                    color: isActive ? module.color : 'rgba(255,255,255,0.6)',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                    }
                  }}
                >
                  <Icon size={17} strokeWidth={isActive ? 2.5 : 2} className="flex-shrink-0 ml-0.5" />
                  <span className="flex-1 whitespace-nowrap font-medium">{module.label}</span>
                  {hasSubMenu && (
                    <ChevronDown
                      size={13}
                      className={cn('flex-shrink-0 transition-transform duration-200', !isOpen && '-rotate-90')}
                    />
                  )}
                </button>

                {hasSubMenu && isOpen && (
                  <div className="ml-5 mt-0.5 mb-1.5 pl-3 space-y-0.5" style={{ borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
                    {sections.map(sec => (
                      <NavLink
                        key={sec.to}
                        to={sec.to}
                        end={sec.end}
                        className={({ isActive: sa }) =>
                          cn(
                            'flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] transition-colors',
                            sa
                              ? 'font-medium'
                              : ''
                          )
                        }
                        style={({ isActive: sa }) => ({
                          backgroundColor: sa ? 'rgba(255,255,255,0.12)' : 'transparent',
                          color: sa ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.5)',
                        })}
                      >
                        {({ isActive: sa }) => (
                          <>
                            <sec.icon size={13} strokeWidth={sa ? 2.5 : 2} className="flex-shrink-0" />
                            {sec.label}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ── 3. Área de usuario ── */}
        <div className="px-2 py-2.5 space-y-0.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {isExpanded ? (
            <>
              <div className="pt-1 pb-2">
                <div
                  className="flex items-center gap-2.5 px-2 py-2 rounded-lg group transition-colors"
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <div className="relative w-8 h-8 flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)' }}>
                      {initial}
                    </div>
                    {fotoSrc && (
                      <img
                        src={fotoSrc}
                        alt={usuario?.nombre}
                        className="absolute inset-0 w-8 h-8 rounded-full object-cover"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                  </div>
                  <div className="flex-1 leading-tight min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'rgba(255,255,255,0.9)' }}>{usuario?.nombre}</p>
                    <p className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      {ROL_LABEL[usuario?.rolId ?? ''] ?? ''}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-1.5 rounded-md transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                    style={{ color: 'rgba(255,255,255,0.45)' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = 'rgb(252,165,165)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
                    title="Cerrar sesión"
                  >
                    <LogOut size={14} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center h-10 rounded-lg transition-colors"
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                title={`${usuario?.nombre} · Cerrar sesión`}
              >
                <div className="relative w-7 h-7">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)' }}>
                    {initial}
                  </div>
                  {fotoSrc && (
                    <img
                      src={fotoSrc}
                      alt={usuario?.nombre}
                      className="absolute inset-0 w-7 h-7 rounded-full object-cover"
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  )}
                </div>
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ── Panel flotante (sidebar contraído) ── */}
      {!isExpanded && floating && floatingModule && (
        <div
          className="fixed z-50 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 w-52"
          style={{ left: '92px', top: `${floating.top}px` }}
          onMouseEnter={() => clearTimeout(closeTimer.current)}
          onMouseLeave={scheduleClose}
        >
          <p
            className="px-3 pt-0.5 pb-1.5 text-[10px] font-semibold uppercase tracking-widest"
            style={{ color: floatingModule.color }}
          >
            {floatingModule.label}
          </p>
          <div className="px-1.5 space-y-0.5">
            {getVisibleSections(floatingModule, usuario?.rolId).map(sec => (
              <NavLink
                key={sec.to}
                to={sec.to}
                end={sec.end}
                onClick={() => setFloating(null)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-600 font-medium'
                      : 'text-text-secondary hover:bg-gray-50 hover:text-text-primary'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <sec.icon size={14} strokeWidth={isActive ? 2.5 : 2} className="flex-shrink-0" />
                    {sec.label}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
